import type { ReactNode, PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { isMobile, isElectron } from 'react-device-detect';
import { FallbackModal } from '../FallbackModal/FallbackModal';
import { useDeepLinking } from '../../hooks/useDeepLinking';

interface Params extends PropsWithChildren {
    onLinked?: () => void;
    onFallback?: () => void;
    children?: ReactNode;
    logoElement?: ReactNode;
    choiceSelectKey?: string;
    relativeDeepUrl: string;
    options: {
        desktopSchema: string;
        desktopPlatformDownloadLink: string;
        mobilePlatformDownloadLink: string;
    };
}

export const DeepLink = ({
    onLinked,
    onFallback,
    children,
    choiceSelectKey,
    relativeDeepUrl,
    options,
    logoElement,
}: Params) => {
    const [showFallback, setShowFallback] = useState(false);

    const { desktopSchema, desktopPlatformDownloadLink, mobilePlatformDownloadLink } = options;

    const handleFallback = useCallback(() => {
        if (choiceSelectKey && localStorage.getItem(choiceSelectKey) === 'browser') {
            onLinked?.();
        } else {
            setShowFallback(true);
            onFallback?.();
        }
    }, [choiceSelectKey, onLinked, onFallback]);

    const onDownload = useCallback(() => {
        window.location.href = isMobile ? mobilePlatformDownloadLink : desktopPlatformDownloadLink;
    }, [desktopPlatformDownloadLink, mobilePlatformDownloadLink]);

    const onBrowserContinue = useCallback(() => {
        if (choiceSelectKey) {
            localStorage.setItem(choiceSelectKey, 'browser');
        }
        setShowFallback(false);
        onLinked?.();
    }, [onLinked, choiceSelectKey]);

    const onTryToOpenInDesktop = useCallback(() => {
        if (!desktopSchema) {
            console.warn('no desktop schema provided');

            return;
        }
        window.location.href = desktopSchema + relativeDeepUrl;
    }, [desktopSchema, relativeDeepUrl]);

    const links = isMobile ? [] : desktopSchema ? desktopSchema + relativeDeepUrl : [];

    useEffect(() => {
        if (isElectron) {
            return;
        }
        const alreadyChosen = choiceSelectKey && localStorage.getItem(choiceSelectKey) === 'browser';
        if (alreadyChosen) {
            onLinked?.();
        }
    }, [onLinked, choiceSelectKey]);

    useDeepLinking({
        links,
        onFallback: handleFallback,
        onLinked,
    });

    return showFallback ? (
        <FallbackModal
            onContinue={onBrowserContinue}
            isMobile={isMobile}
            onDownload={onDownload}
            logoElement={logoElement}
            onTryToOpenInDesktop={onTryToOpenInDesktop}
        />
    ) : (
        <>{children}</>
    );
};
